import { jsx as _jsx } from "react/jsx-runtime";
/**
 * API seam: composes @agnivo/api-client per config.apiMode.
 *
 * Both modes drive the SAME client facade over the SAME route sequence — mock
 * swaps the transport (in-memory) and the reply stream (canned); live adds
 * the real SSE reply stream. Screens never branch on the mode. Dark
 * trust-domain flags at go-live surface as ApiError 404s and the UI degrades
 * per capability.
 *
 * Recovery rule (ADR-081 D2): a CLIENT_STREAM_DROPPED reply error means the
 * assistant reply may still have persisted — consumers re-fetch history WITH
 * RETRY (the persist lands after the delta phase). `refetchHistory` is that
 * primitive; ChatScreen applies it.
 */
import { createContext, useContext, useMemo, useState } from 'react';
import { createClient, createFetchTransport, createSseReplyStream, } from '@agnivo/api-client';
import { useAppConfig } from '../config';
let mockApiModule = null;
export function registerMockApiModule(m) {
    mockApiModule = m;
}
import { createClientEventReporter } from '../telemetry/clientEvent';
import { ClientEventContext } from '../telemetry/ClientEventContext';
import { FunnelStepContext } from '../telemetry/FunnelStepContext';
import { createFunnelStepSender } from '../telemetry/routeViewSender';
import { createUmamiSendWindow } from '../telemetry/umamiSendWindow';
import { useAuth } from '../auth/AuthProvider';
import { ConversationDirectory } from '../lib/conversations';
import { awaitReplyInHistory } from '../lib/recovery';
import { accumulateGenerate } from '../lib/generate';
import { sha256Hex } from '../lib/upload';
// Exported so render tests can inject a controllable `Api` (stub resolve/stream)
// and exercise the real screen orchestration without the mock client's internals.
export const ApiContext = createContext(null);
/** Sibling read facades share the provider's auth, fetch adapter and shell error observer. */
export const ApiTransportContext = createContext(null);
/** Human-readable filename for a captured voice clip, extension from the
 *  container MIME (webm/ogg/mp4 → the browser's native recorder formats). */
function voiceFilename(mime) {
    const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
    return `voice-message.${ext}`;
}
function mockReplyText(userText) {
    return (`Понял: «${userText.slice(0, 120)}». ` +
        'Это демо-ответ: публичный API ещё не открыт, поэтому я отвечаю локально. ' +
        'Интерфейс — настоящий: стриминг, вложения и переключение агентов работают так же, как будут работать с сервером.');
}
function buildApi(client, sendAndStream, apiMode, surface, sendAgentId = false) {
    const directory = new ConversationDirectory(client, surface);
    return {
        client,
        resolveConversation: (agentId) => directory.resolve(agentId),
        forgetConversation: (agentId) => directory.forget(agentId),
        sendAndStream,
        prepareVoiceAttachment: async (audio) => {
            const buf = await audio.blob.arrayBuffer();
            const sha256 = await sha256Hex(buf);
            const sizeBytes = String(audio.blob.size);
            const filename = voiceFilename(audio.mime);
            // Attachment presign is the CONVERSATION trust domain (any authed user),
            // distinct from the knowledge presign; no confirm call — the send confirms.
            const presign = await client.conversations.presignAttachment({
                filename,
                contentType: audio.mime,
                sizeBytes,
            });
            // mock presigns to mock.invalid — PUT only against real storage (mirrors uploadKnowledge)
            if (apiMode === 'live') {
                await client.uploads.putToPresignedUrl(presign, audio.blob);
            }
            return { presignId: presign.fileId, mimeType: audio.mime, sizeBytes, filename, sha256 };
        },
        refetchHistory: (conversationId, opts) => awaitReplyInHistory(client, conversationId, 
        // the deploy gate strips the read-side agent_id too (one flip, all
        // three carries: send, :stream, reads)
        sendAgentId ? opts : { ...opts, agentId: undefined }),
        listHistory: (conversationId, agentId, params) => client.conversations.messages(conversationId, {
            ...params,
            ...(sendAgentId ? { agentId } : {}),
        }),
        uploadKnowledge: async (file, agentId) => {
            const contentType = file.type || 'application/octet-stream';
            const presign = await client.uploads.presign({
                filename: file.name,
                contentType,
                sizeBytes: String(file.size),
            });
            const sha256 = await sha256Hex(await file.arrayBuffer());
            // mock presigns to mock.invalid — the PUT only happens against real storage
            if (apiMode === 'live') {
                await client.uploads.putToPresignedUrl(presign, file);
            }
            await client.knowledge.confirmUpload({
                fileId: presign.fileId,
                filename: file.name,
                contentType,
                sha256,
            });
            const ingested = await client.knowledge.ingest({
                agentId,
                fileId: presign.fileId,
                filename: file.name,
                mimeType: contentType,
            });
            return { fileId: presign.fileId, status: ingested.status };
        },
        createAgentDraft: async (description, onStage) => {
            const { agent } = await accumulateGenerate(client.agentAdmin.generateFromDescription({ description }), undefined, onStage);
            return {
                name: agent.name || 'Новый агент',
                description: agent.description ?? description,
                agent,
            };
        },
        confirmAgentDraft: async (draft) => {
            const { agent, warnings } = await client.agentAdmin.create({
                name: draft.name,
                description: draft.description,
                graphSpec: draft.agent.graphSpec,
                settings: draft.agent.settings ?? undefined,
            });
            if (!agent)
                throw new Error('CreateAgent returned no agent');
            // `?? []` is a WIRE-SHAPE guard, not a policy one: proto3 omits an empty repeated field
            // under some encodings, so an absent `warnings` and `[]` both mean "no warnings" and the
            // caller must not have to tell them apart. It is NOT standing in for a decision — every
            // kind that can arrive has an arm in `saveWarningCopy`.
            return { agent, warnings: warnings ?? [] };
        },
    };
}
export function ApiProvider({ children, mockState, fetchImpl, onApiError, }) {
    const auth = useAuth();
    const config = useAppConfig();
    const { api, transport } = useMemo(() => {
        if (config.apiMode === 'live') {
            // NOTE: coherence (live mode needs a token-bearing provider, not mock auth)
            // is asserted by the SHELL at composition — it owns both the apiMode and
            // which auth provider it plugged. app-core can't tell a mock provider apart.
            const cfg = {
                baseUrl: config.apiBaseUrl,
                getAccessToken: auth.getAccessToken,
                extraHeaders: config.requestHeaders,
                fetchImpl,
                onError: onApiError,
            };
            const transport = createFetchTransport(cfg);
            const client = createClient(transport, (body) => createSseReplyStream(cfg, body));
            const api = buildApi(client, (conversationId, userText, idempotencyKey, agentId, attachments) => client.messages.sendStream({
                conversationId,
                // content only when there IS text — a voice send carries media alone
                // (the intake's text_or_media invariant; gateway!165 threads stream
                // attachments through to agent-core).
                ...(userText ? { content: { type: 'CONTENT_TYPE_TEXT', text: userText } } : {}),
                idempotencyKey,
                ...(attachments?.length ? { attachments } : {}),
                // ADR-092 addressed agent — gated on config.sendAgentId (deploy
                // ordering, see AppConfig docs): the field is spread only when
                // flipped so a pre-ADR-092 gateway (strict SSE unmarshal) never
                // sees an unknown key.
                ...(config.sendAgentId && agentId ? { agentId } : {}),
            }), config.apiMode, config.surface, config.sendAgentId);
            return { api, transport };
        }
        if (mockApiModule === null) {
            throw new Error('apiMode=mock but the mock api module was never registered. In mock builds the shell ' +
                'must registerMockApiModule(await import(\'@agnivo/api-client/mock\')) before React ' +
                'mounts (T1 fold: live builds ship no mock module, so an unregistered mock arm here ' +
                'means a shell skipped its registration).');
        }
        const mock = mockApiModule;
        const transport = mockState ? mock.createMockTransport(mockState) : mock.createMockTransport();
        const api = buildApi(createClient(transport), (conversationId, userText, _idempotencyKey, _agentId, attachments) => mock.createMockReplyStream(conversationId, mockReplyText(userText || (attachments?.length ? 'голосовое сообщение' : ''))), config.apiMode, config.surface);
        return { api, transport };
    }, [auth.getAccessToken, mockState, config.apiMode, config.apiBaseUrl, config.requestHeaders, config.surface, config.sendAgentId, fetchImpl, onApiError]);
    // Identity-stable per config: the reporter OWNS the tab's token bucket, so
    // rebuilding it refills the budget. Rebuilding on every render would make the
    // cap unenforceable while every unit test still passed — the bucket would be
    // correct and never consulted twice. `auth.getAccessToken` is documented
    // identity-stable by the shells for exactly this reason (see mini-app's
    // `useTelegramInitDataAuth`), so it is a safe dependency.
    const reportClientEvent = useMemo(() => createClientEventReporter({
        apiMode: config.apiMode,
        baseUrl: config.apiBaseUrl,
        surface: config.surface,
        getAccessToken: auth.getAccessToken,
        fetchImpl,
    }), [config.apiMode, config.apiBaseUrl, config.surface, auth.getAccessToken, fetchImpl]);
    // ⛔ A REF, NOT `useMemo` — and the difference from `reportClientEvent` twenty
    // lines up is deliberate rather than an inconsistency, so it is worth stating.
    //
    // React may DISCARD a `useMemo` at will (documented), and StrictMode's dev
    // double-mount is exactly when it would. The client-event reporter above tolerates
    // that because its budget is a rate limit: a refilled bucket over-admits briefly.
    // This sender owns SESSION once-flags, so a discarded sender forgets that a step
    // already fired and the step fires again — which does not lose data, it INFLATES
    // ACTIVATION. An over-count on a funnel is worse than an under-count, because it
    // moves the number in the direction everyone wants to believe.
    //
    // ⇒ Same treatment `RouteViewReporter` gives its own sender, for the same reason,
    // and it is the stricter of the two patterns already in this codebase.
    // ⓘ A `useState` LAZY INITIALIZER rather than the lazy-ref idiom: a state
    // initializer is guaranteed to run once and is never a render-phase ref write,
    // which `react-hooks` rejects. It also pins the sender to the config it was built
    // with — correct here, because the once-flags are session state and rebuilding on
    // a config change is the discard case this whole comment is about.
    // Same once-only discipline as the sender below, and for a strictly stronger reason than
    // config pinning: this object is a CENSUS, not a rate limit. A discarded window does not
    // over-admit like a refilled bucket — it drops the in-flight attempts it was holding and
    // starts from zero, so StrictMode's dev double-mount would silently under-report while every
    // test still passed. A `useMemo` is documented as discardable; this must not be one.
    //
    // ⚠️ It captures the first mount's `reportClientEvent`. If that memo is later rebuilt on a
    // config change, reports go to the older reporter instance — a valid reporter for the same
    // surface, which is why this is recorded rather than fixed with a ref indirection.
    const [umamiWindow] = useState(() => createUmamiSendWindow({ report: reportClientEvent }));
    const [reportFunnelStep] = useState(() => {
        const analytics = config.analytics;
        if (analytics === undefined)
            return () => { };
        return createFunnelStepSender({
            apiMode: config.apiMode,
            collectorUrl: analytics.collectorUrl,
            websiteId: analytics.websiteId,
            clientShell: config.clientShell,
            hostname: analytics.hostname,
            umamiWindow,
        });
    });
    return (_jsx(ClientEventContext.Provider, { value: reportClientEvent, children: _jsx(FunnelStepContext.Provider, { value: reportFunnelStep, children: _jsx(ApiTransportContext.Provider, { value: transport, children: _jsx(ApiContext.Provider, { value: api, children: children }) }) }) }));
}
export function useApiTransport() {
    const transport = useContext(ApiTransportContext);
    if (!transport)
        throw new Error('useApiTransport outside <ApiProvider>');
    return transport;
}
export function useApi() {
    const ctx = useContext(ApiContext);
    if (!ctx)
        throw new Error('useApi outside <ApiProvider>');
    return ctx;
}
//# sourceMappingURL=ApiProvider.js.map